"use client";

import { useLoggedUser } from "@/context/AuthContext";
import { toggleFavorite as toggleFavoriteApi } from "@/services/dwellFinderApi/favoritesActions";
import {
  ensureFavoritesLoaded,
  isCachedFavorite,
  setCachedFavorite,
} from "@/lib/favoritesCache";
import { useEffect, useState } from "react";

/**
 * Reusable hook that encapsulates the favourite toggle logic
 * (fetch status + optimistic save/unsave) used across
 * PropertyCard, Card, and the property details page.
 *
 * Instead of firing one `GET /favorites/{id}/check` per card,
 * this hook reads from a shared cache that is populated by a
 * single `GET /favorites` call (see `lib/favoritesCache.ts`).
 */
export function useFavoriteToggle(
  postId: string | undefined,
  options?: {
    /** Called when the user isn't logged in and tries to toggle */
    onAuthRequired?: () => void;
    /** Called after a successful toggle */
    onToggled?: () => void;
    /** Pre-seed the favourite state (e.g. items on the saved page are already favourited) */
    initialFavorited?: boolean;
  },
) {
  const [isFavorite, setIsFavorite] = useState(options?.initialFavorited ?? false);
  const { currentUser, loading } = useLoggedUser();

  // Load the shared favorites cache once, then read from it.
  // All mounted hooks share the same single network request.
  useEffect(() => {
    if (!postId || !currentUser || options?.initialFavorited) return;

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        await ensureFavoritesLoaded();
        if (!cancelled) setIsFavorite(isCachedFavorite(postId));
      } catch (error) {
        console.error("Failed to fetch favorite status:", error);
      }
    };

    fetchStatus();
    return () => { cancelled = true; };
  }, [postId, currentUser]);

  // Optimistic toggle — also updates the shared cache so other
  // components that mount later see the correct state immediately.
  const toggleFavorite = async () => {
    if (!currentUser && !loading) {
      options?.onAuthRequired?.();
      return;
    }

    if (!postId) return;

    const newState = !isFavorite;
    setIsFavorite(newState);
    setCachedFavorite(postId, newState);

    try {
      const response = await toggleFavoriteApi(postId);
      // Sync with server truth if available
      if (typeof response.isFavorited === "boolean") {
        setIsFavorite(response.isFavorited);
        setCachedFavorite(postId, response.isFavorited);
      }
      options?.onToggled?.();
    } catch (error) {
      console.error("Error toggling favorite:", error);
      setIsFavorite(!newState);
      setCachedFavorite(postId, !newState); // Rollback cache too
    }
  };

  return { isFavorite, toggleFavorite };
}
